import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../api/client";
import { AppHeader, Breadcrumb } from "../components/AppHeader";
import { DoiButton } from "../components/DoiButton";
import { ItemSprite } from "../components/ItemSprite";
import { RewardsModal } from "../components/RewardsModal";
import { useResponsiveLayout } from "../logic/useResponsiveLayout";
import { playSfx } from "../logic/sfx";
import { simplePanelStyle } from "../theme/simplePanel";
import { E32 } from "../theme/endesga32";
import { factionFor } from "../theme/factions";
import { pixelTheme } from "../theme/pixel";

interface DerivedSlot {
  index: number;
  item: api.ItemDefinition | null;
  count: number;
  /** Position within a multi-slot stack: 0 for the only/last slot of an
   *  item, n>0 for additional spillover slots. Only used for stable
   *  React keys. */
  spillIdx: number;
}

// Turn a `grant_bundle` open's `extra.granted` payload into the
// item/currency rows + faction-unlock notes the RewardsModal renders.
export function bundleRewardFrom(
  extra: Record<string, unknown> | null,
  itemsById: Map<string, api.ItemDefinition>,
): { entries: api.NpcGrantedEntry[]; notes: string[] } {
  const granted = (extra?.granted ?? null) as api.BundleGranted | null;
  const entries: api.NpcGrantedEntry[] = [];
  const notes: string[] = [];
  if (!granted) return { entries, notes };

  for (const { item_id, count } of granted.items ?? []) {
    const def = itemsById.get(item_id);
    entries.push({
      kind: "item",
      item_id,
      count,
      name: def?.name ?? item_id,
      sprite: def?.sprite ?? null,
    });
  }
  if (granted.gold > 0) {
    entries.push({ kind: "currency", currency: "gold", amount: granted.gold });
  }
  if (granted.void_juice > 0) {
    entries.push({
      kind: "currency",
      currency: "void_juice",
      amount: granted.void_juice,
    });
  }
  for (const faction of granted.unlocked_factions ?? []) {
    notes.push(`${factionFor(faction).name} faction unlocked!`);
  }
  return { entries, notes };
}

export function InventoryPage() {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const { narrow } = useResponsiveLayout();
  const [user, setUser] = useState<api.User | null>(null);
  const [data, setData] = useState<api.InventoryResponse | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  // Local food cooldown — set after a successful eat (3s) and ticked
  // down once per 100ms. The button shows a countdown while > 0 and
  // refuses with a notice sound when clicked. The server is still
  // authoritative; a 429 on use sets remainingMs to the server's
  // value so the two stay in sync if they ever drift.
  const [cooldownMs, setCooldownMs] = useState(0);
  const cooldownTickRef = useRef<number | null>(null);
  // Result of opening a `grant_bundle` item (chest / welcome bundle /
  // unlock potion) — drives the rewards popup. Null when none is open.
  const [bundleReward, setBundleReward] = useState<{
    entries: api.NpcGrantedEntry[];
    notes: string[];
  } | null>(null);

  useEffect(() => {
    if (cooldownMs <= 0) {
      if (cooldownTickRef.current != null) {
        window.clearInterval(cooldownTickRef.current);
        cooldownTickRef.current = null;
      }
      return;
    }
    if (cooldownTickRef.current != null) return;
    cooldownTickRef.current = window.setInterval(() => {
      setCooldownMs((ms) => Math.max(0, ms - 100));
    }, 100);
    return () => {
      if (cooldownTickRef.current != null) {
        window.clearInterval(cooldownTickRef.current);
        cooldownTickRef.current = null;
      }
    };
  }, [cooldownMs > 0]);

  useEffect(() => {
    void (async () => {
      try {
        const [me, inv] = await Promise.all([api.getMe(), api.getInventory()]);
        setUser(me.user);
        setData(inv);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load inventory.");
      }
    })();
  }, []);

  const reload = useCallback(async () => {
    const [me, inv] = await Promise.all([api.getMe(), api.getInventory()]);
    setUser(me.user);
    setData(inv);
  }, []);

  const itemsById = data
    ? new Map(data.items.map((i) => [i.id, i] as const))
    : new Map<string, api.ItemDefinition>();

  const slots: DerivedSlot[] = data
    ? data.slots.map((s) => ({
        index: s.slot_index,
        item: s.item_id ? (itemsById.get(s.item_id) ?? null) : null,
        count: s.count,
        spillIdx: 0,
      }))
    : [];

  const usedCount = slots.filter((s) => s.count > 0).length;
  const capacity = data?.capacity ?? 20;
  const selected = slots.find((s) => s.index === selectedSlot) ?? null;

  const onUse = useCallback(async () => {
    if (selected == null || selected.item == null) return;
    // Pack items are not consumed via the use endpoint — the pack
    // opening page consumes them atomically with the roll. The button
    // here is just a navigation shortcut.
    if (selected.item.use?.kind === "rip_open_pack") {
      navigate(`/packs/${encodeURIComponent(selected.item.id)}`);
      return;
    }
    setActionBusy(true);
    setError(null);
    const wasFood =
      selected.item.use?.kind === "heal" || selected.item.use?.kind === "consume";
    const itemsById = data
      ? new Map(data.items.map((i) => [i.id, i] as const))
      : new Map<string, api.ItemDefinition>();
    try {
      const res = await api.useInventorySlot(selected.index);
      if (wasFood) playSfx("munch");
      if (res.use_kind === "grant_bundle") {
        setBundleReward(bundleRewardFrom(res.extra, itemsById));
      }
      await reload();
      if (wasFood) setCooldownMs(3_000);
    } catch (e) {
      // 429 cooldown: surface the remaining time on the button via
      // the cooldown timer. No banner — the button itself shows the
      // state, so there's no layout shift.
      if (e instanceof api.ApiError && e.body && typeof e.body === "object") {
        const body = e.body as { error?: string; remaining_ms?: number };
        if (body.error === "cooldown" && typeof body.remaining_ms === "number") {
          setCooldownMs(body.remaining_ms);
        } else {
          setError(e.message);
        }
      } else {
        setError(e instanceof Error ? e.message : "Use failed.");
      }
    } finally {
      setActionBusy(false);
    }
  }, [navigate, reload, selected, data]);

  const onDrop = useCallback(async () => {
    if (selected == null || selected.item == null) return;
    setActionBusy(true);
    setError(null);
    try {
      await api.dropInventorySlot(selected.index, selected.count);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Drop failed.");
    } finally {
      setActionBusy(false);
    }
  }, [reload, selected]);

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        padding: "20px 24px",
      }}
    >
      <div style={{ maxWidth: 980, margin: "0 auto", display: "grid", gap: 16 }}>
        <AppHeader
          user={user}
          onBack={() => {
            if (window.history.length > 1) navigate(-1);
            else navigate("/lobby");
          }}
        />
        <Breadcrumb text="INVENTORY" />

        {error && (
          <div
            style={{
              padding: 10,
              background: theme.bgDeep,
              border: `2px solid ${E32.scarlet}`,
              color: E32.scarlet,
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: theme.letterSpacing,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "grid",
            // Side-by-side on desktop, stacked on narrow screens.
            gridTemplateColumns: narrow ? "1fr" : "1fr 320px",
            gap: 16,
            alignItems: "start",
          }}
        >
          {/* Inventory grid */}
          <div style={{ ...simplePanelStyle(theme), padding: 16 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 10,
                fontFamily: theme.fontDisplay,
                fontSize: 11,
                letterSpacing: theme.letterSpacing,
              }}
            >
              <span>INVENTORY</span>
              <span
                style={{
                  color:
                    usedCount >= capacity ? E32.scarlet : theme.inkFaint,
                }}
              >
                {usedCount}/{capacity} SLOTS
              </span>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
                gap: 6,
              }}
            >
              {slots.map((slot) => (
                <SlotCell
                  key={slot.index}
                  slot={slot}
                  theme={theme}
                  selected={selectedSlot === slot.index}
                  onClick={() =>
                    slot.item ? setSelectedSlot(slot.index) : undefined
                  }
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          <div
            style={{
              ...simplePanelStyle(theme),
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              minHeight: 380,
            }}
          >
            <div
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 11,
                letterSpacing: theme.letterSpacing,
              }}
            >
              ITEM PREVIEW
            </div>
            {selected && selected.item ? (
              <ItemPreview
                slot={selected}
                onUse={onUse}
                onDrop={onDrop}
                busy={actionBusy}
                cooldownMs={cooldownMs}
                theme={theme}
              />
            ) : (
              <div
                style={{
                  fontSize: 16,
                  color: theme.inkFaint,
                  fontFamily: theme.fontBody,
                }}
              >
                Select an item to preview.
              </div>
            )}
          </div>
        </div>
      </div>

      {bundleReward && (
        <RewardsModal
          entries={bundleReward.entries}
          notes={bundleReward.notes}
          title="OPENED"
          onClose={() => setBundleReward(null)}
        />
      )}
    </div>
  );
}

function SlotCell({
  slot,
  theme,
  selected,
  onClick,
}: {
  slot: DerivedSlot;
  theme: typeof pixelTheme;
  selected: boolean;
  onClick: () => void;
}) {
  const empty = slot.item == null;
  const interactive = !empty;
  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? selected : undefined}
      aria-label={
        interactive
          ? `${slot.item!.name}, ${slot.count} in stock`
          : undefined
      }
      onClick={empty ? undefined : onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      title={empty ? "Empty" : `${slot.item!.name} ×${slot.count}`}
      style={{
        position: "relative",
        aspectRatio: "1",
        // Both filled and empty slots use the parchment fill so an
        // empty slot is still clearly a *slot*, not blank space.
        // Selected slots replace the plum border with the accent.
        background: theme.bg,
        border: `2px solid ${selected ? theme.accent : theme.panelEdge}`,
        boxShadow:
          "inset 1px 1px 0 rgba(255,255,255,0.5), inset -1px -1px 0 rgba(0,0,0,0.18)",
        cursor: empty ? "default" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {!empty && slot.item && (
        <>
          <ItemSprite id={slot.item.id} size={64} />
          {slot.count > 1 && (
            <div
              style={{
                position: "absolute",
                top: 2,
                right: 2,
                fontFamily: theme.fontDisplay,
                fontSize: 9,
                color: theme.bg,
                background: theme.ink,
                border: `1px solid ${theme.ink}`,
                padding: "1px 3px",
                letterSpacing: "0.04em",
              }}
            >
              {slot.count}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ItemPreview({
  slot,
  onUse,
  onDrop,
  busy,
  cooldownMs,
  theme,
}: {
  slot: DerivedSlot;
  onUse: () => void;
  onDrop: () => void;
  busy: boolean;
  cooldownMs: number;
  theme: typeof pixelTheme;
}) {
  const item = slot.item!;
  const isFood =
    item.use?.kind === "heal" || item.use?.kind === "consume";
  const useOnCooldown = isFood && cooldownMs > 0;
  // Round up to one decimal for display (so 2900ms shows "2.9s",
  // not "2.8999s" or "3.0s").
  const cooldownLabel = useOnCooldown
    ? `${(Math.ceil(cooldownMs / 100) / 10).toFixed(1)}S`
    : null;
  return (
    <>
      <div
        style={{
          aspectRatio: "1",
          background: theme.bgDeep,
          border: `2px solid ${theme.ink}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <ItemSprite id={item.id} size={128} />
        <div
          style={{
            position: "absolute",
            bottom: 4,
            right: 4,
            fontFamily: theme.fontDisplay,
            fontSize: 10,
            color: theme.bg,
            background: theme.ink,
            padding: "2px 6px",
            letterSpacing: theme.letterSpacing,
          }}
        >
          ×{slot.count}
        </div>
      </div>
      <div>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 12,
            letterSpacing: theme.letterSpacing,
            color: theme.ink,
          }}
        >
          {item.name.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 8,
            color: theme.inkFaint,
            letterSpacing: theme.letterSpacing,
            marginTop: 4,
          }}
        >
          {item.display_category.toUpperCase()}
        </div>
      </div>
      <div style={{ fontSize: 16, lineHeight: 1.4, color: theme.ink }}>
        {item.description}
      </div>
      {item.use?.kind === "heal" && item.use.health_restore != null && (
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: E32.fern,
            marginTop: 2,
          }}
        >
          RESTORES {item.use.health_restore} HP
        </div>
      )}
      {item.flavor && (
        <div
          style={{
            fontStyle: "italic",
            fontSize: 14,
            lineHeight: 1.4,
            color: theme.inkFaint,
            paddingTop: 4,
            borderTop: `1px dashed ${theme.panelEdge}`,
          }}
        >
          {item.flavor}
        </div>
      )}
      <div style={{ display: "flex", gap: 6, marginTop: "auto", flexWrap: "wrap" }}>
        {item.use && (
          <DoiButton
            variant="primary"
            disabled={busy || useOnCooldown}
            onClick={onUse}
          >
            {cooldownLabel ?? item.use.label}
          </DoiButton>
        )}
        <DoiButton variant="default" disabled={busy} onClick={onDrop}>
          DROP
        </DoiButton>
      </div>
    </>
  );
}
